import type { ReactElement, ReactNode } from "react";
import {
  aggregateWeaponStats,
  armorMaxDurability,
  compatibleHosts,
  containerCapacity,
  fittedModsDeep,
  getItemDef,
  isTrueMagazine,
  loadedAmmo,
  loadedRounds,
  loadedRoundsKnown,
  magCountKnown,
  recoilMultiplier,
  sellPriceDeep,
  usedCells,
  weaponMaxDurability,
  wearExtraSpreadDeg,
  type ArmorItemDef,
  type AttachKind,
  type CaliberId,
  type ContainerItemDef,
  type Inventory,
  type ItemDef,
  type ItemInstance,
  type ItemKind,
  type MedEffect,
  type StatMods,
  type TraderDef,
  type WeaponItemDef,
} from "@rietz/shared";
import { formatEuros } from "../meta/format.js";
import { useHud, type OpenContainer } from "../store.js";
import { FONT, PANEL_BG, PANEL_LINE, TEXT, TEXT_DIM } from "../theme.js";
import { instanceAt, uiViewportSize, viewportToUi, type ItemSource } from "./drag.js";
import { formatKg } from "./quickmove.js";

/**
 * What the thing under the cursor is: the numbers a 44-pixel tile cannot show.
 * INERT (`pointer-events: none`), above the drag ghost; the screen suppresses
 * it while a drag or context menu is live.
 */

/** Milliseconds the pointer must rest on a tile before the panel appears. */
export const TOOLTIP_DELAY_MS = 400;

/** Roughly the panel's width; used to decide which side of the cursor it goes. */
const TIP_W = 240;

/** What the screen decided to describe, and where the pointer was resting. */
export interface TooltipState {
  iid: string;
  source: ItemSource;
  /** Viewport pixels. */
  px: number;
  py: number;
}

export const KIND_LABELS: Record<ItemKind, string> = {
  weapon: "Weapon",
  ammo: "Ammunition",
  armor: "Armor",
  med: "Medical",
  container: "Container",
  loot: "Loot",
  attachment: "Attachment",
  grenade: "Grenade",
};

/**
 * Caliber ids as the roster spells them. Exported for the market tab's
 * ammunition shelves — one table, one spelling.
 */
export const CALIBER_LABELS: Record<CaliberId, string> = {
  "8x57": "8×57",
  "9x19": "9×19",
  "12g": "12/70",
  "7.62x39": "7.62×39",
};

export interface ItemTooltipProps {
  tip: TooltipState;
  inv: Inventory;
  container: OpenContainer | null;
  /** Whose counter the player is standing at, if any. Only the trader screen
   *  passes one; everywhere else the panel describes the item alone. */
  trader?: TraderDef | null;
}

export function ItemTooltip(props: ItemTooltipProps): ReactElement | null {
  const { tip } = props;
  const trader = props.trader ?? null;
  // Read FIRST and unconditionally — every return below is an early one.
  const inRaid = useHud((s) => s.screen === "raid");
  // The item can be gone by the time the delay elapses — another looter emptied
  // the crate, the server resynced — and a tooltip for nothing is worse than none.
  const item = instanceAt(props.inv, props.container, tip.source, tip.iid);
  if (item === null) return null;
  const def = getItemDef(item.defId);
  if (def === null) return null;

  // Clamped by ANCHORING: past the middle of the viewport the panel hangs off
  // the cursor's other side.
  const point = viewportToUi(tip.px, tip.py);
  const viewport = uiViewportSize();
  const flipX = point.x + TIP_W + 24 > viewport.width;
  const flipY = point.y > viewport.height / 2;

  return (
    <div
      style={{
        position: "fixed",
        left: flipX ? undefined : point.x + 14,
        right: flipX ? Math.max(8, viewport.width - point.x + 14) : undefined,
        top: flipY ? undefined : point.y + 16,
        bottom: flipY ? Math.max(8, viewport.height - point.y + 16) : undefined,
        maxWidth: TIP_W,
        padding: "8px 10px",
        font: FONT,
        color: TEXT,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        borderRadius: 4,
        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.55)",
        pointerEvents: "none",
        // Above the ghost (40), which is the only other thing that floats.
        zIndex: 60,
      }}
    >
      <div style={{ fontSize: 13, color: "#f4f8fc", overflowWrap: "anywhere" }}>{def.name}</div>
      <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 2 }}>
        {KIND_LABELS[def.kind]} · {def.w}×{def.h}
      </div>

      <ItemFacts def={def} item={item} trader={trader} inRaid={inRaid} />

      {def.description === undefined ? null : (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: `1px solid ${PANEL_LINE}`,
            fontSize: 11,
            color: TEXT_DIM,
            overflowWrap: "anywhere",
          }}
        >
          {def.description}
        </div>
      )}
    </div>
  );
}

/**
 * The numbers, as a block — shared by the hover tooltip and the Inspect dialog
 * so the two never disagree.
 */
export function ItemFacts(props: {
  def: ItemDef;
  item: ItemInstance;
  trader?: TraderDef | null;
  /**
   * The shelf's asking price for an item looked at BEFORE it is owned. See
   * `InspectDialog.buyPrice`.
   */
  buyPrice?: number | null;
  /**
   * In a raid, an uncounted magazine prints "?" instead of its count. A prop so
   * the block stays pure; defaults to the stash's exact counts.
   */
  inRaid?: boolean;
}): ReactElement {
  const { def, item } = props;
  const trader = props.trader ?? null;
  const asking = props.buyPrice ?? null;
  const inRaid = props.inRaid === true;
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
      <KindRows def={def} item={item} inRaid={inRaid} />
      <Row label="Weight" value={weightText(def, item)} />
      <Row label="Value" value={`€${def.valueEuro}`} />
      {/* Only at a counter. "Value" is what the item is worth to the valley;
          this is what the person standing in front of you will actually hand
          over for it, which is a different number at every shop — and at some
          of them no number at all. */}
      {trader === null ? null : <Row label="Sells for" value={sellText(trader, item)} />}
      {/* The other direction, and only for a thing still on the shelf: what
          this counter wants for it. Directly under "Value" so the mark-up is a
          subtraction the player can do with their eyes. */}
      {asking === null ? null : <Row label="Buy price" value={formatEuros(asking)} />}
    </div>
  );
}

/**
 * What this trader pays for THIS instance — stack, mods and contents
 * (`sellPriceDeep`). A refusal prints dim, like a label rather than a number.
 */
function sellText(trader: TraderDef, item: ItemInstance): ReactNode {
  const paid = sellPriceDeep(trader, item);
  if (paid === null) return <span style={{ color: TEXT_DIM }}>{"won't buy"}</span>;
  return formatEuros(paid);
}

/**
 * One label/value line. The label never wraps; the value may — lists are meant
 * to spill onto a second line.
 */
function Row(props: { label: string; value: ReactNode }): ReactElement {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between", fontSize: 11 }}>
      <span style={{ color: TEXT_DIM, whiteSpace: "nowrap" }}>{props.label}</span>
      <span style={{ textAlign: "right", overflowWrap: "anywhere" }}>{props.value}</span>
    </div>
  );
}

/**
 * The guns this part fits, by short name — "—" when nothing takes it (a fact
 * worth printing).
 */
function hostsText(partId: string): string {
  const hosts = compatibleHosts(partId);
  if (hosts.length === 0) return "—";
  return hosts.map((w) => w.shortName).join(", ");
}

/**
 * The rows that only make sense for one kind of thing. Only what applies — an
 * absent row is the context menu's absent entry.
 */
function KindRows(props: {
  def: ItemDef;
  item: ItemInstance;
  /** See `ItemFacts.inRaid`: a round count nobody has counted reads "?". */
  inRaid: boolean;
}): ReactElement | null {
  const { def, item, inRaid } = props;

  switch (def.kind) {
    case "weapon": {
      // Both halves of "how full" come off the FITTED MAGAZINE via the
      // accessors.
      const ammo = loadedAmmo(def, item);
      const rounds = loadedRounds(def, item);
      // The AGGREGATED magazine size: a 40-round box holds forty. Zero for an
      // empty well, and "0/0" is what the player needs to see.
      const magSize = aggregateWeaponStats(def, item).magSize;
      // EVERY part on the rifle, nested included, in the order the stats folded
      // in.
      const fitted = fittedModsDeep(def, item);
      const extraSpread = wearExtraSpreadDeg(def, item);
      return (
        <>
          <Row label="Caliber" value={CALIBER_LABELS[def.caliber]} />
          <Row
            label="Fire mode"
            value={def.fireMode === "auto" ? "Automatic" : "Semi-automatic"}
          />
          {/* As a percentage of the carbine, parts folded in — the same scale
              the Werkbank's recoil row prints, so a gun picked up off a corpse
              can be compared against the one in the player's hands. */}
          <Row
            label="Recoil"
            value={`${Math.round(def.recoil * recoilMultiplier(def, item) * 100)} %`}
          />
          {/* "?/30" in a raid for a box nobody has looked into since it was
              last fired — the HUD's withholding, in the other place the same
              number is printed. An empty well and a full box both answer
              "known", so "0/0" and "30/30" are never hidden. */}
          <Row
            label="Magazine"
            value={
              inRaid && !loadedRoundsKnown(def, item)
                ? `?/${magSize}`
                : `${rounds}/${magSize}`
            }
          />
          {ammo === null ? null : <Row label="Loaded" value={ammo.shortName} />}
          {/* CONDITION, printed exactly the way the plate's is — same row, same
              bracket for a rebuilt ceiling, same `fmtDurability` — because it
              is the same number with the same meaning and a player who has
              learned to read it on a vest must not have to learn it again on a
              rifle. A gun that has never been fired has no `durability` key at
              all and reads as its ceiling, which is why this row is
              unconditional: "100/100" is a fact about the gun, not a blank. */}
          <Row label="Durability" value={durabilityText(def, item)} />
          {/* What the wear is actually COSTING, in the only currency a shooter
              cares about. Absent until it starts — a gun above
              `WEAR_ACCURACY_FRAC` of its ceiling pays nothing, and a row of
              "+0.0°" on every fresh rifle would be a permanent reminder of a
              penalty that is not being charged. */}
          {extraSpread === 0 ? null : (
            <Row label="Wear spread" value={`+${extraSpread.toFixed(2)}°`} />
          )}
          {/* What is bolted on, by name. A player comparing two carbines on a
              corpse needs this before anything else on the panel: the numbers
              above are already the modified ones, so without the list there is
              nothing to explain why one of them is better. */}
          {fitted.length === 0 ? null : (
            <Row label="Attachments" value={fitted.map((f) => f.def.shortName).join(", ")} />
          )}
        </>
      );
    }
    case "ammo":
      return (
        <>
          <Row label="Caliber" value={CALIBER_LABELS[def.caliber]} />
          <Row label="Damage" value={def.damage} />
          <Row label="Penetration" value={def.penPower} />
          {def.pellets > 1 ? <Row label="Pellets" value={`×${def.pellets}`} /> : null}
        </>
      );
    case "armor":
      return (
        <>
          <Row label="Armor class" value={def.armorClass} />
          <Row label="Durability" value={durabilityText(def, item)} />
        </>
      );
    case "med":
      return (
        <>
          <Row label="Uses" value={`${item.durability ?? def.charges}/${def.charges}`} />
          <Row label="Use time" value={`${def.useTimeSec} s`} />
          <Row label="Effect" value={effectLabel(def.effect)} />
        </>
      );
    case "container":
      // "Contents" only when there are some: absent contents are an empty box,
      // and the capacity row above has already said how big empty is.
      return (
        <>
          <Row label="Capacity" value={capacityLabel(def)} />
          {item.contents === undefined ? null : (
            <Row
              label="Contents"
              value={`${usedCells(item.contents)}/${containerCapacity(def)} cells`}
            />
          )}
        </>
      );
    case "attachment": {
      // A TRUE MAGAZINE is an object, not a block of numbers: capacity and
      // cartridge come off the def and instance, never `modifiers`.
      const mag = isTrueMagazine(def) ? def : null;
      // Rounds print only when there ARE some — for a FITTED magazine too; the
      // rounds live on this instance wherever it is.
      const rounds = item.mag ?? 0;
      const loaded = item.ammoDefId === undefined ? null : getItemDef(item.ammoDefId);
      // How many, or "?" for an uncounted box in a raid. The CARTRIDGE half is
      // never withheld.
      const countText = inRaid && mag !== null && !magCountKnown(mag, item) ? "?" : rounds;
      return (
        <>
          <Row label="Slot" value={ATTACH_LABELS[def.attachKind]} />
          {/* WHICH GUNS, immediately under which kind of mounting point,
              because the two together are the only question a loose part in the
              stash raises: the slot label says what sort of thing it is, and
              this says whether anything the player owns can wear it. Deep, so
              a red dot lists the Eastern carbine it can only reach through a
              railed top cover — see `compatibleHosts`. */}
          <Row label="Compatible with" value={hostsText(def.id)} />
          {/* Caliber then capacity, the same two rows in the same order the
              weapon block leads with — a magazine is judged on exactly those
              before anything else. */}
          {mag === null ? null : <Row label="Caliber" value={CALIBER_LABELS[mag.caliber]} />}
          {mag === null ? null : (
            <Row label="Capacity" value={`${mag.magCapacity} rounds`} />
          )}
          {/* The `rounds === 0` guard stays exactly as it was: an empty magazine
              is a KNOWN magazine (`magCountKnown`), so a "?" can never land on
              the branch that prints nothing at all. */}
          {mag === null || rounds === 0 ? null : (
            <Row
              label="Loaded"
              value={
                loaded === null
                  ? `${countText} rounds`
                  : `${countText} × ${loaded.shortName}`
              }
            />
          )}
          {modRows(def.modifiers)}
        </>
      );
    }
    case "grenade":
      return (
        <>
          <Row label="Fuse" value={`${def.fuseSec} s`} />
          <Row label="Radius" value={`${def.effect.radiusM} m`} />
          {def.effect.t === "frag" ? (
            <Row label="Damage" value={def.effect.damage} />
          ) : (
            <Row label="Duration" value={`${def.effect.durationSec} s`} />
          )}
        </>
      );
    case "loot": {
      // A grocery: only the `consume` block is worth a row (a barter good first
      // — see `LootItemDef.consume`). A meter it does nothing for prints
      // NOTHING rather than "+0".
      const c = def.consume;
      if (c === undefined) return null;
      return (
        <>
          <Row label="Use time" value={`${c.useTimeSec} s`} />
          {c.energy > 0 ? <Row label="Energy" value={`+${c.energy}`} /> : null}
          {c.hydration > 0 ? <Row label="Hydration" value={`+${c.hydration}`} /> : null}
        </>
      );
    }
  }
}

const ATTACH_LABELS: Record<AttachKind, string> = {
  optic: "Optic",
  muzzle: "Muzzle",
  grip: "Grip",
  stock: "Stock",
  mag: "Magazine",
  barrel: "Barrel",
  handguard: "Handguard",
  pistolgrip: "Pistol grip",
  receiver: "Receiver",
  tactical: "Tactical",
};

/**
 * One row per stat an attachment touches. Multipliers print as the signed
 * percentage CHANGE; absent modifiers print nothing. Ergonomics leads because
 * it is bare points on the 0–100 scale, not a percentage.
 */
function modRows(m: StatMods): ReactNode {
  const rows: ReactElement[] = [];
  const pct = (label: string, mult: number | undefined): void => {
    if (mult === undefined) return;
    const delta = Math.round((mult - 1) * 100);
    rows.push(<Row key={label} label={label} value={`${delta > 0 ? "+" : "−"}${Math.abs(delta)} %`} />);
  };
  if (m.ergoAdd !== undefined) {
    const sign = m.ergoAdd > 0 ? "+" : "−";
    rows.push(
      <Row key="ergo" label="Ergonomics" value={`${sign}${Math.abs(m.ergoAdd)}`} />,
    );
  }
  pct("Hip-fire spread", m.hipSpreadMult);
  pct("Recoil", m.recoilMult);
  pct("Muzzle velocity", m.muzzleVelocityMult);
  pct("Reload time", m.reloadSecMult);
  if (m.zeroRangeSetM !== undefined) {
    rows.push(<Row key="zero" label="Zeroing" value={`${m.zeroRangeSetM} m`} />);
  }
  if (m.zoom !== undefined) rows.push(<Row key="zoom" label="Magnification" value={`${m.zoom}×`} />);
  if (m.suppressed === true) rows.push(<Row key="sup" label="Sound" value="suppressed" />);
  return rows;
}

/**
 * "31.3/38.8 (40)": current, repaired ceiling, and in brackets the born-with
 * maximum once the two have parted. One function for plates and guns; absent
 * `durability` reads as the ceiling.
 */
function durabilityText(def: ArmorItemDef | WeaponItemDef, item: ItemInstance): string {
  const max =
    def.kind === "armor" ? armorMaxDurability(def, item) : weaponMaxDurability(def, item);
  return (
    `${fmtDurability(item.durability ?? max)}/${fmtDurability(max)}` +
    (item.maxDurability === undefined ? "" : ` (${def.maxDurability})`)
  );
}

/**
 * A durability number at the precision it has: whole numbers stay whole,
 * fractions get one decimal (all `applyRepair` produces).
 */
function fmtDurability(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * What a container holds: "4×2" for one open compartment, "6 pouches, 12 cells"
 * for a pouched one — the pouch count decides whether a drum fits.
 */
function capacityLabel(def: ContainerItemDef): string {
  const layout = def.layout;
  if (layout === undefined) return `${def.gridW}×${def.gridH}`;
  const pouches = layout.length === 1 ? "1 pouch" : `${layout.length} pouches`;
  return `${pouches}, ${containerCapacity(def)} cells`;
}

function effectLabel(effect: MedEffect): string {
  switch (effect.t) {
    case "stopBleed":
      return "Stops bleeding";
    case "fixFracture":
      return "Splints fracture";
    case "heal":
      return "Healing";
  }
}

/** Per unit, plus what the stack actually costs you to carry. */
function weightText(def: ItemDef, item: ItemInstance): string {
  const unit = formatKg(def.weightKg);
  if (item.stack <= 1) return unit;
  return `${unit} × ${item.stack} = ${formatKg(def.weightKg * item.stack)}`;
}
